/** Fixture cadence helpers (league rules + league builder). */

export const WEEKDAYS = [
  { id: 0, key: 'sun', label: 'Sunday' },
  { id: 1, key: 'mon', label: 'Monday' },
  { id: 2, key: 'tue', label: 'Tuesday' },
  { id: 3, key: 'wed', label: 'Wednesday' },
  { id: 4, key: 'thu', label: 'Thursday' },
  { id: 5, key: 'fri', label: 'Friday' },
  { id: 6, key: 'sat', label: 'Saturday' },
];

export const FIXTURE_CADENCE_OPTIONS = [
  { id: 'weekly', label: 'Weekly', hint: 'One fixture round per week on the chosen weekday.' },
  { id: 'twice_weekly', label: 'Twice weekly', hint: 'Two fixture rounds per week on the chosen weekdays.' },
  { id: 'fortnightly', label: 'Fortnightly', hint: 'Every two weeks on the chosen weekday.' },
  { id: 'monthly', label: 'Monthly', hint: 'Roughly every four weeks on the chosen weekday.' },
];

export function weekdayIdFromKey(key) {
  const row = WEEKDAYS.find((d) => d.key === key);
  return row ? row.id : 4;
}

export function dateIsoOnly(date) {
  return date.toISOString().slice(0, 10);
}

function parseDateOnly(dateStr) {
  return new Date(`${dateStr}T00:00:00`);
}

function toDateOnly(d) {
  return dateIsoOnly(d);
}

export function normalizeMatchStartTime(value, fallback = '19:30') {
  const raw = String(value || '').trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw);
  if (!m) return fallback;
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const min = Math.min(59, Math.max(0, Number(m[2])));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function allowedWeekdaysForCadence(cadence, weekday1, weekday2) {
  if (cadence === 'twice_weekly') {
    return new Set([weekdayIdFromKey(weekday1), weekdayIdFromKey(weekday2)]);
  }
  return new Set([weekdayIdFromKey(weekday1)]);
}

/**
 * Build ISO date strings for successive fixture rounds.
 * @param {number} count
 * @param {{ cadence: string, weekday1: string, weekday2?: string, startDate: string }} opts
 */
export function buildFixtureDateSlots(count, opts) {
  const cadence = String(opts?.cadence || 'weekly');
  const weekday1 = String(opts?.weekday1 || 'thu');
  const weekday2 = String(opts?.weekday2 || 'tue');
  const startDate = String(opts?.startDate || dateIsoOnly(new Date()));
  const allowed = allowedWeekdaysForCadence(cadence, weekday1, weekday2);
  const dates = [];
  const start = parseDateOnly(startDate);
  const cursor = new Date(start);
  let guard = 0;

  while (dates.length < count && guard < 5000) {
    const ymd = toDateOnly(cursor);
    if (allowed.has(cursor.getDay())) {
      let accept = true;
      if (cadence === 'fortnightly') {
        const daysSince = Math.floor((cursor.getTime() - start.getTime()) / 86400000);
        accept = Math.floor(daysSince / 7) % 2 === 0;
      } else if (cadence === 'monthly' && dates.length > 0) {
        const last = parseDateOnly(dates[dates.length - 1]);
        const gap = Math.floor((cursor.getTime() - last.getTime()) / 86400000);
        accept = gap >= 28;
      }
      if (accept) dates.push(ymd);
    }
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return dates;
}

export function formatFixtureDateLabel(dateStr) {
  return parseDateOnly(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function buildFixtureSchedulePreview(count, opts) {
  const time = normalizeMatchStartTime(opts?.matchStartTime);
  const slots = buildFixtureDateSlots(count, opts);
  return slots.map((date, i) => ({
    round: i + 1,
    date,
    time,
    label: `Round ${i + 1}`,
    when: `${formatFixtureDateLabel(date)} · ${time}`,
  }));
}

export const CHECKOUT_OPTIONS = [
  { id: '', label: 'Default (league)' },
  { id: 'double-out', label: 'DOUBLE' },
  { id: 'straight-out', label: 'STRAIGHT' },
  { id: 'master-out', label: 'MASTER' },
];

export function resolveGameCheckout(gameRow, leagueDefault = 'double-out') {
  const rowVal = String(gameRow?.checkout || '').trim();
  if (!rowVal || rowVal === 'default') return String(leagueDefault || 'double-out');
  return rowVal;
}
